import { useTranslation } from 'react-i18next';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import FlightIcon from '@mui/icons-material/Flight';
import LandscapeIcon from '@mui/icons-material/Landscape';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/joy';

// Mock data for performance metrics
const mockMetrics = {
  flightHours: {
    value: 128.5,
    unit: 'hours',
    change: 12.3,
    trend: 'up'
  },
  areaCovered: {
    value: 1250,
    unit: 'hectares',
    change: 8.7,
    trend: 'up'
  },
  imagesCaptured: {
    value: 15680,
    unit: 'images',
    change: 15.2,
    trend: 'up'
  },
  campaignSuccessRate: {
    value: 94.8,
    unit: '%',
    change: -2.1,
    trend: 'down'
  }
};

interface PerformanceMetricsProps {
  metrics?: typeof mockMetrics;
}

const PerformanceMetrics = ({ metrics = mockMetrics }: PerformanceMetricsProps) => {
  const { t } = useTranslation();

  // Function to format numbers with commas
  const formatNumber = (num: number) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // Function to get the appropriate icon for each metric
  const getMetricIcon = (metricKey: string) => {
    switch (metricKey) {
      case 'flightHours':
        return <FlightIcon fontSize='medium' />;
      case 'areaCovered':
        return <LandscapeIcon fontSize='medium' />;
      case 'imagesCaptured':
        return <CameraAltIcon fontSize='medium' />;
      case 'campaignSuccessRate':
        return <TaskAltIcon fontSize='medium' />;
      default:
        return null;
    }
  };

  return (
    <Card variant='outlined'>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography level='title-md'>{t('dashboard.performance_metrics', 'Performance Metrics')}</Typography>
      </Box>
      <CardContent>
        <Grid container spacing={2}>
          {Object.entries(metrics).map(([key, metric]) => (
            <Grid key={key} md={3} xs={6}>
              <Card sx={{ height: '100%' }} variant='soft'>
                <CardContent>
                  <Stack spacing={1}>
                    <Box sx={{ color: 'primary.500', display: 'flex', justifyContent: 'center' }}>
                      {getMetricIcon(key)}
                    </Box>
                    <Typography level='title-lg' textAlign='center'>
                      {key === 'flightHours' ? metric.value.toFixed(1) : formatNumber(Math.round(metric.value))}
                      <Typography component='span' level='body-xs'>
                        {' '}
                        {metric.unit}
                      </Typography>
                    </Typography>
                    <Typography level='body-sm' textAlign='center'>
                      {t(`dashboard.metric_${key}`, key.replace(/([A-Z])/g, ' $1').trim())}
                    </Typography>
                    <Stack alignItems='center' direction='row' justifyContent='center' spacing={0.5}>
                      {metric.trend === 'up' ? (
                        <ArrowUpwardIcon color='success' fontSize='small' />
                      ) : (
                        <ArrowDownwardIcon color='error' fontSize='small' />
                      )}
                      <Typography color={metric.trend === 'up' ? 'success' : 'danger'} level='body-xs'>
                        {metric.change > 0 ? '+' : ''}
                        {metric.change}%
                      </Typography>
                      <Typography color='neutral' level='body-xs'>
                        {t('dashboard.vs_last_month', 'vs last month')}
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default PerformanceMetrics;
